"use client"

import { useEffect, useRef } from "react"
import { useSignedInSession } from "@/hooks/useSignedInSession"
import {
    useDesktopStore,
    useWindows,
    useActiveWindowId,
    useDesktopActions,
    useDesktopInitialize,
    useIsRehydrated,
} from "@/stores/desktopStore"
import {
    WindowType,
    WindowState,
    WindowManagerStore,
    WINDOW_TYPES,
    LEGACY_WINDOW_TYPES,
} from "./types"
import { DOCUMENT_TEMPLATE_DOC_TYPES } from "@/constants/documentTemplate.constants"
import {
    explorerSearchParamsFromWindowData,
    explorerWindowDataFromSearchParams,
} from "./windows/explorer/explorer.utils"

import { ROUTES } from "@/constants/routes.constants"
import { WALLET_FUND_PARAM } from "@/utils/walletFundLink.utils"

export const DESKTOP_PATH = ROUTES.home
export const URL_SYNC_DELAY_MS = 500

function hasStringId(obj: unknown): obj is { id: string } {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "id" in obj &&
        typeof (obj as Record<string, unknown>).id === "string"
    )
}

type DeepLinkWindowType = WindowType | (typeof LEGACY_WINDOW_TYPES)[number]

interface WindowOpenConfig {
    type: DeepLinkWindowType
    data?: Record<string, unknown>
}

const DEEP_LINK_WINDOW_TYPES: readonly DeepLinkWindowType[] = [
    ...WINDOW_TYPES,
    ...LEGACY_WINDOW_TYPES,
]

interface WindowUrlCodec {
    parse: (
        searchParams: URLSearchParams
    ) => { data: Record<string, unknown> | undefined } | undefined
    build: (
        params: URLSearchParams,
        data: Record<string, unknown> | undefined
    ) => void
}

const WINDOW_URL_CODECS: Partial<Record<WindowType, WindowUrlCodec>> = {
    explorer: {
        parse: searchParams => ({
            data: explorerWindowDataFromSearchParams(searchParams),
        }),
        build: (params, data) => {
            for (const [key, value] of explorerSearchParamsFromWindowData(
                data
            )) {
                params.set(key, value)
            }
        },
    },
    comms: {
        parse: searchParams => {
            const threadId = searchParams.get("threadId")
            const mailbox = searchParams.get("mailbox")
            return {
                data:
                    threadId || mailbox
                        ? {
                              ...(threadId && { threadRef: threadId }),
                              ...(mailbox && { mailbox }),
                          }
                        : undefined,
            }
        },
        build: (params, data) => {
            if (typeof data?.mailbox === "string") {
                params.set("mailbox", data.mailbox)
            }
            if (typeof data?.threadRef === "string") {
                params.set("threadId", data.threadRef)
            }
        },
    },
    document: {
        parse: searchParams => {
            const docId = searchParams.get("docId")
            return { data: docId ? { documentId: docId } : undefined }
        },
        build: (params, data) => {
            let docId: string | null = null
            if (typeof data?.documentId === "string") {
                docId = data.documentId
            } else if (hasStringId(data?.document)) {
                docId = data.document.id
            }
            if (docId) params.set("docId", docId)
        },
    },
    cartagePay: {
        parse: searchParams => {
            const tab = searchParams.get("tab")
            const fund = searchParams.get(WALLET_FUND_PARAM)
            const view = searchParams.get("view")
            return {
                data: tab
                    ? { tab, view: view ?? undefined, ...(fund && { fund }) }
                    : undefined,
            }
        },
        build: (params, data) => {
            if (typeof data?.tab === "string" && data.tab !== "wallet") {
                params.set("tab", data.tab)
                if (
                    data.tab === "statements" &&
                    typeof data.view === "string"
                ) {
                    params.set("view", data.view)
                }
            }
        },
    },
    report: {
        parse: searchParams => {
            const ref = searchParams.get("ref")
            return ref ? { data: { referenceNumber: ref } } : undefined
        },
        build: (params, data) => {
            if (typeof data?.referenceNumber === "string") {
                params.set("ref", data.referenceNumber)
            }
        },
    },
    skill: {
        parse: searchParams => {
            const slug = searchParams.get("skill")
            return slug ? { data: { slug } } : undefined
        },
        build: (params, data) => {
            if (typeof data?.slug === "string") {
                params.set("skill", data.slug)
            }
        },
    },
    template: {
        parse: searchParams => {
            const kind = searchParams.get("kind") ?? "pdf"
            if (kind === "email") {
                const templateKey = searchParams.get("templateKey")
                return templateKey
                    ? { data: { kind: "email", templateKey } }
                    : undefined
            }
            const docType = searchParams.get("docType")
            if (
                !docType ||
                !(DOCUMENT_TEMPLATE_DOC_TYPES as readonly string[]).includes(
                    docType
                )
            ) {
                return undefined
            }
            return { data: { kind: "pdf", docType } }
        },
        build: (params, data) => {
            if (
                data?.kind === "email" &&
                typeof data.templateKey === "string"
            ) {
                params.set("kind", "email")
                params.set("templateKey", data.templateKey)
            } else if (
                data?.kind === "pdf" &&
                typeof data.docType === "string"
            ) {
                params.set("kind", "pdf")
                params.set("docType", data.docType)
            }
        },
    },
}

export function parseWindowsFromUrl(
    searchParams: URLSearchParams
): WindowOpenConfig[] {
    const windowTypesParam = searchParams.get("w")
    if (!windowTypesParam) return []

    const threadId = searchParams.get("threadId")

    const windowTypes = windowTypesParam
        .split(",")
        .map(t => t.trim().toLowerCase())
        .map(t => DEEP_LINK_WINDOW_TYPES.find(w => w.toLowerCase() === t))
        .filter((t): t is DeepLinkWindowType => t !== undefined)

    const configs: WindowOpenConfig[] = []

    for (const type of windowTypes) {
        if (type === "mail") {
            configs.push({ type, data: threadId ? { threadId } : undefined })
            continue
        }
        if (type === "knowledgeDoc" || type === "knowledgeNote") {
            continue
        }

        const codec = WINDOW_URL_CODECS[type as WindowType]
        if (!codec) {
            configs.push({ type, data: undefined })
            continue
        }

        const parsed = codec.parse(searchParams)
        if (!parsed) continue
        configs.push({ type, data: parsed.data })
    }

    return configs
}

export function shouldSkipRestore(searchParams: URLSearchParams): boolean {
    return searchParams.get("restore") === "false"
}

export function buildWindowUrl({
    type,
    data,
}: {
    type: WindowType
    data?: Record<string, unknown>
}): string {
    const params = new URLSearchParams()
    params.set("w", type)
    WINDOW_URL_CODECS[type]?.build(params, data)
    return `${window.location.origin}${DESKTOP_PATH}?${params.toString()}`
}

export function buildUrlFromActiveWindow(
    windows: WindowState[],
    activeWindowId: string | null
): string {
    if (!activeWindowId) return ""

    const activeWindow = windows.find(w => w.id === activeWindowId)
    if (!activeWindow) return ""

    const params = new URLSearchParams()
    params.set("w", activeWindow.type)

    WINDOW_URL_CODECS[activeWindow.type]?.build(params, activeWindow.data)

    return params.toString()
}

import { DockAppConfig } from "./types"
import {
    DOCK_APP_ORDER,
    WINDOW_REGISTRY_METADATA as WINDOW_REGISTRY,
} from "./windowRegistry.metadata"

// Labels are vestigial — Dock.tsx overrides them with tApps(app.id) translations
const DOCK_APP_LABELS: Record<(typeof DOCK_APP_ORDER)[number], string> = {
    setup: "Setup",
    explorer: "Explorer",
    reports: "Reports",
    wilson: "Wilson",
    comms: "Comms",
    todos: "Routines",
    warehouseScheduling: "Docks",
    freightDashboard: "Dashboard",
    cartagePay: "Wallet",
    settings: "Settings",
}

export const DOCK_APPS: DockAppConfig[] = DOCK_APP_ORDER.map(type => ({
    id: type,
    type,
    label: DOCK_APP_LABELS[type],
    iconSrc: WINDOW_REGISTRY[type].icon?.iconSrc,
    fallbackIcon: WINDOW_REGISTRY[type].icon?.fallbackIcon,
}))

export function useWindowManager(): WindowManagerStore {
    const windows = useWindows()
    const activeWindowId = useActiveWindowId()
    const actions = useDesktopActions()

    return {
        windows,
        activeWindowId,

        stageBack: actions.stageBack,

        openWindow: actions.openWindow,
        closeWindow: actions.closeWindow,
        closeAllWindows: actions.closeAllWindows,
        focusWindow: actions.focusWindow,
        updateTitle: actions.updateTitle,
        updateWindowData: actions.updateWindowData,
    }
}

interface UseWindowManagerStateOptions {
    searchParams?: URLSearchParams | null
}

export function useWindowManagerState(
    options?: UseWindowManagerStateOptions
): WindowManagerStore {
    const { orgId } = useSignedInSession()
    const initialize = useDesktopInitialize()
    const store = useWindowManager()
    const openWindow = useDesktopStore(state => state.openWindow)
    const updateWindowData = useDesktopStore(state => state.updateWindowData)
    const isRehydrated = useIsRehydrated()

    const lastRestoredParamsRef = useRef<string | null>(null)

    useEffect(() => {
        if (orgId) {
            initialize(orgId)
        }
    }, [orgId, initialize])

    useEffect(() => {
        if (!orgId || !isRehydrated || !options?.searchParams) return

        const paramsKey = options.searchParams.toString()

        if (lastRestoredParamsRef.current === paramsKey) return

        const windowConfigs = parseWindowsFromUrl(options.searchParams)
        if (windowConfigs.length === 0) return

        lastRestoredParamsRef.current = paramsKey

        windowConfigs.forEach(config => {
            const windowId = openWindow(config.type, {
                data: config.data,
                source: "url",
            })
            if (config.data) updateWindowData(windowId, config.data)
        })
    }, [
        orgId,
        isRehydrated,
        options?.searchParams,
        openWindow,
        updateWindowData,
    ])

    return store
}
